/* eslint-disable @next/next/no-img-element */
"use client";

import DOMPurify from "dompurify";
import React, { useEffect, useState } from "react";
import { parseString } from "xml2js";

interface FeedItem {
  title: string;
  link: string;
  description: string;
  pubDate: string;
  category: string;
}

interface RssChannel {
  item: {
    category: string[];
    description: string[];
    link: string[];
    pubDate: string[];
    title: string[];
  }[];
}

interface RssResult {
  rss: {
    channel: RssChannel[];
  };
}

interface SanitizeOptions {
  text: string;
  maxLength: number;
}

export default function LinksRssFeed(): React.JSX.Element {
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchRssFeed = async (): Promise<void> => {
      try {
        const response = await fetch("/api/raindrop-rss-feed");
        const xmlData = await response.text();
        parseString(xmlData, (err: Error | null, result: RssResult) => {
          if (err) {
            console.error("Failed to parse RSS feed:", err);
            return;
          }
          const items: FeedItem[] =
            result.rss.channel[0]?.item?.map((item) => ({
              category: item.category[0] ?? "",
              description: item.description[0] ?? "",
              link: item.link[0] ?? "",
              pubDate: item.pubDate[0] ?? "",
              title: item.title[0] ?? "",
            })) ?? [];
          setFeedItems(items);
        });
      } catch (error) {
        console.error("Failed to fetch RSS feed:", error);
      }
    };

    void fetchRssFeed();
  }, []);

  const extractImageFromDescription = (
    description: string,
  ): { imageUrl: string | null; cleanDescription: string } => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(description, "text/html");
    const img = doc.querySelector("img");
    const imageUrl = img ? img.src : null;
    if (img) {
      img.remove(); // Remove the image from the description
    }
    const cleanDescription = doc.body.innerHTML;
    return { cleanDescription, imageUrl };
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
      .replace(/(\w+) (\d+), (\d+)/, "$1 $2, $3");
  };

  const extractDomain = (url: string): string => {
    try {
      const domain = new URL(url).hostname;
      return domain.startsWith("www.") ? domain.slice(4) : domain;
    } catch {
      return url; // Return the original URL if it's invalid
    }
  };

  const sanitizeAndCleanDescription = ({ text, maxLength }: SanitizeOptions): string => {
    // First, sanitize the HTML
    const sanitizedHtml = DOMPurify.sanitize(text);

    // Remove leading <br> tags
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sanitizedHtml;

    while (tempDiv.firstChild && tempDiv.firstChild.nodeName === "BR") {
      tempDiv.removeChild(tempDiv.firstChild);
    }

    // Get the text content
    let cleanText = tempDiv.textContent || tempDiv.innerText || "";

    // Truncate to the last complete sentence within maxLength characters
    if (cleanText.length > maxLength) {
      // Find the last sentence end within maxLength characters
      const lastSentenceEnd = cleanText.substring(0, maxLength).lastIndexOf(".");
      if (lastSentenceEnd > 0) {
        cleanText = cleanText.substring(0, lastSentenceEnd + 1);
      } else {
        // If no sentence end found, just truncate at maxLength characters
        cleanText = cleanText.substring(0, maxLength) + "...";
      }
    }

    // Wrap the truncated text in a paragraph tag
    return cleanText;
  };

  const handleImageLoad = (url: string): void => {
    setLoadedImages((prev) => ({ ...prev, [url]: true }));
  };

  const handleImageError = (url: string): void => {
    setLoadedImages((prev) => ({ ...prev, [url]: false }));
  };

  return (
    <ul className="rss-feed">
      {feedItems.map((item, index) => {
        const { imageUrl, cleanDescription } = extractImageFromDescription(item.description);
        const domain = extractDomain(item.link);
        const finalDescription = sanitizeAndCleanDescription({
          maxLength: 200,
          text: cleanDescription,
        });
        return (
          <li key={index}>
            <article className="feed-item" itemType="http://schema.org/Article" itemScope>
              <div className="feed-item-cell">
                <dl className="headline-with-date">
                  <dt>Headline:</dt>
                  <dd className="headline">
                    <a href={item.link} itemProp="url" rel="noopener noreferrer" target="_blank">
                      <span itemProp="headline">{item.title}</span>
                    </a>
                  </dd>
                  <dt>Date Published:</dt>
                  <dd className="date">
                    <time dateTime={item.pubDate} itemProp="datePublished">
                      {formatDate(item.pubDate)}
                    </time>
                  </dd>
                </dl>

                <p dangerouslySetInnerHTML={{ __html: finalDescription }} itemProp="description" />
                {/* <p className="category">
                  Category: <span itemProp="about">{item.category}</span>
                </p> */}
                <span
                  className="source"
                  itemProp="publisher"
                  itemType="http://schema.org/Organization"
                  itemScope
                >
                  <a href={item.link} itemProp="name" rel="noopener noreferrer" target="_blank">
                    {domain}
                  </a>
                </span>
              </div>
              {imageUrl && (
                <div
                  className="feed-item-cell feed-item-cell-image"
                  data-is-valid-image={loadedImages[imageUrl]}
                >
                  <a href={item.link} itemProp="url" rel="noopener noreferrer" target="_blank">
                    {loadedImages[imageUrl] === undefined && <span>Loading...</span>}
                    <img
                      alt={item.title}
                      itemProp="image"
                      src={imageUrl}
                      style={{ display: loadedImages[imageUrl] ? "block" : "none" }}
                      onError={() => handleImageError(imageUrl)}
                      onLoad={() => handleImageLoad(imageUrl)}
                    />
                  </a>
                </div>
              )}
            </article>
          </li>
        );
      })}
    </ul>
  );
}
